import React from 'react';

interface JobRole {
    title: string;
    department: string;
    location: string;
    type: string;
    description: string;
    responsibilities: string[];
    requirements: string[];
    applicationUrl: string;
}

const CareersPage: React.FC = () => {
    const jobRoles: JobRole[] = [
        {
            title: "Research & Modeling Intern",
            department: "Research & Modeling",
            location: "San Francisco, CA",
            type: "Internship",
            description: "Gain hands-on experience in quantitative research and financial modeling while working alongside our experienced research team.",
            responsibilities: [
                "Assist with data collection and analysis projects",
                "Support development of research models and strategies",
                "Prepare research reports and presentations",
                "Participate in team meetings and strategy discussions",
                "Learn about financial markets and investment processes"
            ],
            requirements: [
                "Currently pursuing degree in Finance, Economics, Mathematics, or related field",
                "Strong analytical and quantitative skills",
                "Proficiency in Excel and basic programming knowledge",
                "Excellent communication and presentation skills",
                "Genuine interest in financial markets and technology",
                "Proficiency with modern AI tools and platforms, with demonstrated ability to integrate them effectively into daily workflows while understanding their capabilities, applications, and limitations"
            ],
            applicationUrl: "https://pastasauce.notion.site/2002472aefc58072bb02f7ed12e3a989"
        },
        {
            title: "Full Stack Software Engineering Intern",
            department: "Internal Systems, Data & Integration",
            location: "San Francisco, CA / Remote",
            type: "Internship",
            description: "Join our engineering team to build and enhance internal systems, data pipelines, and integration platforms that power our investment operations.",
            responsibilities: [
                "Develop and maintain internal tools and automation systems",
                "Build data integration pipelines and ETL processes",
                "Support API development and system integrations",
                "Assist with database design and optimization projects",
                "Collaborate with senior engineers on technical architecture decisions",
                "Participate in code reviews and agile development processes"
            ],
            requirements: [
                "Currently pursuing degree in Computer Science, Software Engineering, or related field",
                "Proficiency in at least one programming language (Python, JavaScript, TypeScript preferred)",
                "Understanding of databases, APIs, and web technologies",
                "Familiarity with version control systems (Git)",
                "Strong problem-solving skills and attention to detail",
                "Interest in financial technology and data systems",
                "Proficiency with modern AI tools and platforms, with demonstrated ability to integrate them effectively into daily workflows while understanding their capabilities, applications, and limitations"
            ],
            applicationUrl: "https://pastasauce.notion.site/2002472aefc58072bb02f7ed12e3a989"
        },
        {
            title: "Senior Software Engineer",
            department: "Forward Deployed Technology",
            location: "San Francisco, CA / Remote",
            type: "Full-time",
            description: "Join our engineering team to build cutting-edge financial technology solutions that power the future of investment management.",
            responsibilities: [
                "Design and develop scalable backend systems and APIs",
                "Collaborate with product and design teams to deliver exceptional user experiences",
                "Implement robust data processing and analytics pipelines",
                "Mentor junior engineers and contribute to technical architecture decisions"
            ],
            requirements: [
                "5+ years of software engineering experience",
                "Proficiency in Python, TypeScript, or similar languages",
                "Experience with cloud platforms (AWS, GCP, or Azure)",
                "Strong understanding of database design and optimization",
                "Experience with financial systems is a plus",
                "Proficiency with modern AI tools and platforms, with demonstrated ability to integrate them effectively into daily workflows while understanding their capabilities, applications, and limitations"
            ],
            applicationUrl: ""
        },
        {
            title: "Data Scientist",
            department: "Research & Analytics",
            location: "New York, NY / Remote",
            type: "Full-time",
            description: "Drive data-driven investment decisions by developing sophisticated models and analytics for our quantitative trading strategies.",
            responsibilities: [
                "Develop and maintain quantitative models for investment strategies",
                "Analyze large datasets to identify market patterns and opportunities",
                "Create data visualizations and reports for stakeholders",
                "Collaborate with portfolio managers to optimize trading algorithms"
            ],
            requirements: [
                "PhD or Master's in Data Science, Statistics, Mathematics, or related field",
                "3+ years of experience in quantitative finance or similar field",
                "Proficiency in Python, R, and SQL",
                "Experience with machine learning & deep learning frameworks (PyTorch, TensorFlow, Scikit-learn, Keras)",
                "Strong understanding of financial markets and instruments",
                "Proficiency with modern AI tools and platforms, with demonstrated ability to integrate them effectively into daily workflows while understanding their capabilities, applications, and limitations"
            ],
            applicationUrl: ""
        },
        {
            title: "Investment Analyst",
            department: "Investment Management",
            location: "San Francisco, CA",
            type: "Full-time",
            description: "Support investment decisions through comprehensive market research, financial modeling, and due diligence processes.",
            responsibilities: [
                "Conduct detailed financial analysis and market research",
                "Build financial models and perform valuation analyses",
                "Prepare investment memos and presentations",
                "Monitor portfolio performance and risk metrics",
                "Support due diligence processes for new investments"
            ],
            requirements: [
                "Bachelor's degree in Finance, Economics, or related field",
                "2+ years of investment banking, private equity, or asset management experience",
                "Strong financial modeling and analytical skills",
                "Proficiency in Excel, Bloomberg, and financial databases",
                "CFA designation preferred but not required",
                "Proficiency with modern AI tools and platforms, with demonstrated ability to integrate them effectively into daily workflows while understanding their capabilities, applications, and limitations"
            ],
            applicationUrl: ""
        },
        {
            title: "Product Manager",
            department: "Product",
            location: "San Francisco, CA / Remote",
            type: "Full-time",
            description: "Lead product strategy and development for our investment technology platform, working closely with engineering and design teams.",
            responsibilities: [
                "Define product roadmap and strategy for investment platforms",
                "Collaborate with engineering and design teams to deliver features",
                "Conduct user research and gather feedback from stakeholders",
                "Analyze product metrics and optimize user experience",
                "Work with compliance and risk teams to ensure regulatory compliance"
            ],
            requirements: [
                "5+ years of product management experience",
                "Experience with financial services or fintech products",
                "Strong analytical and problem-solving skills",
                "Excellent communication and stakeholder management abilities",
                "Technical background with ability to work closely with engineers",
                "Proficiency with modern AI tools and platforms, with demonstrated ability to integrate them effectively into daily workflows while understanding their capabilities, applications, and limitations"
            ],
            applicationUrl: ""
        },
        {
            title: "Marketing Specialist",
            department: "Marketing",
            location: "Remote",
            type: "Full-time",
            description: "Drive brand awareness and lead generation through strategic marketing initiatives and content creation.",
            responsibilities: [
                "Develop and execute comprehensive marketing strategies",
                "Create compelling content for various marketing channels",
                "Manage social media presence and community engagement",
                "Coordinate with sales team to generate qualified leads",
                "Analyze marketing performance and optimize campaigns"
            ],
            requirements: [
                "3+ years of marketing experience, preferably in financial services",
                "Strong writing and content creation skills",
                "Experience with digital marketing tools and analytics",
                "Knowledge of SEO, SEM, and social media marketing",
                "Bachelor's degree in Marketing, Business, or related field",
                "Proficiency with modern AI tools and platforms, with demonstrated ability to integrate them effectively into daily workflows while understanding their capabilities, applications, and limitations"
            ],
            applicationUrl: ""
        }
    ];

    const benefits = [
        "Competitive salary and equity packages",
        "Comprehensive health, dental, and vision insurance",
        "Flexible work arrangements and remote options",
        "Professional development and learning opportunities",
        "Cutting-edge technology and tools",
        "Collaborative and innovative work environment"
    ];

    return (
        <div className="max-w-6xl mx-auto px-4 py-12 md:py-24">
            <h1 className="text-4xl md:text-5xl font-bold mb-12 tracking-tighter">
                Join Our <span className="text-accent" style={{
                    color: '#3F013D',
                    textShadow: '1px 1px 0 #ae4094, -1px -1px 0 #000, 0px 1px 0 #ae4094, 1px 0px 0 #ae4094, 0px -1px 0 #000, -1px 0px 0 #000'
                }}>Team</span>
            </h1>

            <p className="text-xl md:text-2xl text-gray-300 mb-16 max-w-3xl">
                We're building the future of investment technology. Join us in creating innovative solutions
                that transform how capital is allocated and managed.
            </p>

            {/* Benefits Section */}
            <div className="mb-16 bg-gray-900 bg-opacity-50 p-8 rounded-lg">
                <h2 className="text-3xl font-bold mb-6">Why Work With Us?</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {benefits.map((benefit, index) => (
                        <div key={index} className="flex items-center space-x-3">
                            <div className="w-2 h-2 bg-[#ff00c3] rounded-full flex-shrink-0"></div>
                            <span className="text-gray-300">{benefit}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Job Roles Section */}
            <div className="mb-16">
                <h2 className="text-3xl font-bold mb-8">Open Positions</h2>
                <div className="space-y-8">
                    {jobRoles.map((role, index) => (
                        <div key={index} className="bg-gray-900 bg-opacity-50 rounded-lg p-8 hover:bg-opacity-70 transition-all">
                            <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start mb-6">
                                <div className="mb-4 lg:mb-0">
                                    <h3 className="text-2xl font-bold mb-2">{role.title}</h3>
                                    <div className="flex flex-wrap gap-4 text-sm">
                                        <span className="bg-accent bg-opacity-20 text-accent-light px-3 py-1 rounded-full">
                                            {role.department}
                                        </span>
                                        <span className="bg-gray-700 px-3 py-1 rounded-full text-gray-300">
                                            {role.location}
                                        </span>
                                        <span className="bg-blue-900 bg-opacity-50 px-3 py-1 rounded-full text-blue-300">
                                            {role.type}
                                        </span>
                                        {role.type === "Internship" && (
                                            <>
                                                <span className="bg-purple-900 bg-opacity-50 text-purple-300 px-3 py-1 rounded-full text-xs font-medium flex items-center">
                                                    Un-Paid Role
                                                </span>
                                                <span className="bg-green-900 bg-opacity-50 text-green-300 px-3 py-1 rounded-full text-xs font-medium flex items-center">
                                                    Remote/WFH
                                                </span>
                                            </>
                                        )}
                                    </div>
                                </div>
                                {role.applicationUrl ? (
                                    <a
                                        href={role.applicationUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-block py-3 px-6 bg-accent text-white font-medium rounded-lg hover:bg-opacity-90 transition-colors whitespace-nowrap text-center w-full lg:w-auto"
                                    >
                                        Apply Now
                                    </a>
                                ) : (
                                    <button
                                        disabled
                                        className="inline-block py-3 px-6 bg-gray-600 text-gray-300 font-medium rounded-lg cursor-not-allowed whitespace-nowrap text-center w-full lg:w-auto"
                                    >
                                        Application Closed
                                    </button>
                                )}
                            </div>

                            <p className="text-gray-300 mb-6">{role.description}</p>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                <div>
                                    <h4 className="text-lg font-semibold mb-3 text-accent-light">Key Responsibilities</h4>
                                    <ul className="space-y-2">
                                        {role.responsibilities.map((resp, respIndex) => (
                                            <li key={respIndex} className="flex items-start space-x-3">
                                                <div className="w-1.5 h-1.5 bg-[#ff00c3] rounded-full mt-2 flex-shrink-0"></div>
                                                <span className="text-gray-300">{resp}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                <div>
                                    <h4 className="text-lg font-semibold mb-3 text-accent-light">Requirements</h4>
                                    <ul className="space-y-2">
                                        {role.requirements.map((req, reqIndex) => (
                                            <li key={reqIndex} className="flex items-start space-x-3">
                                                <div className="w-1.5 h-1.5 bg-[#ff00c3] rounded-full mt-2 flex-shrink-0"></div>
                                                <span className="text-gray-300">{req}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Contact Section */}
            <div className="bg-gray-900 bg-opacity-50 p-8 rounded-lg">
                <h2 className="text-3xl font-bold mb-6">Don't See Your Role?</h2>
                <p className="text-gray-300 mb-6">
                    We're always looking for exceptional talent. If you're passionate about finance, technology,
                    and innovation, we'd love to hear from you even if there isn't a current opening that matches
                    your background.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                    <a
                        href="mailto:info+careers@samsarainc.com?subject=Resume: Application for Samsara Capital Partners&body=Dear Samsara Team,%0D%0A%0D%0AWhat you're good at / passionate about:%0D%0A[Please describe your key skills, expertise, and what you're most passionate about in your professional work]%0D%0A%0D%0AHow you believe you can contribute:%0D%0A[Explain how your skills and experience would add value to Samsara and help achieve our mission]%0D%0A%0D%0AHow you envision your role:%0D%0A[Describe how you see yourself fitting into the team and what impact you hope to make]%0D%0A%0D%0AWhat are your expectations for involvement, compensation, etc.?%0D%0A[Share your expectations regarding role responsibilities, growth opportunities, compensation range, or any other important considerations]%0D%0A%0D%0AWhy we should choose you:%0D%0A[Highlight what makes you unique and why you'd be an exceptional addition to the team]%0D%0A%0D%0A[PLEASE ATTACH YOUR CV BELOW]%0D%0A%0D%0ABest regards,%0D%0A[Your Name]"
                        className="inline-block py-3 px-6 bg-accent text-white font-medium rounded-lg hover:bg-opacity-90 transition-colors text-center"
                    >
                        Send Us Your Resume
                    </a>
                    <a
                        href="https://forms.gle/samsara-general-inquiry"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block py-3 px-6 border border-accent-light text-accent-light font-medium rounded-lg hover:bg-accent hover:text-white transition-colors text-center"
                    >
                        General Inquiry
                    </a>
                </div>
            </div>
        </div>
    );
};

export default CareersPage; 